/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: WeakPasswordsPage.tsx
 🗓️ Created: 2025-01-21
 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
 ────────────────────────────────────────────────────────────────*/

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, Smartphone } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import DataGridSimple from '../../components/ui/DataGridSimple';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { fetchWeakPasswords, WeakPasswordReport } from './api';
import { useNavigationStore } from '../../store/navigation';

export default function WeakPasswordsPage() {
    const { t } = useTranslation();
    const { showError, showSuccess } = useNotifications();
    const { setCurrentPageTitle } = useNavigationStore();
    const { selectedDomain } = useContextStore();
    const [data, setData] = useState<WeakPasswordReport[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state (client-side)
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');

    // Effective domain ID for contextual filtering
    const effectiveDomainId = selectedDomain?.id;

    const loadData = useCallback(async (silent = false) => {
        setLoading(true);
        try {
            const reports = await fetchWeakPasswords(effectiveDomainId);
            setData(reports);
            if (!silent) {
                showSuccess(t('common.refreshed', 'Data refreshed successfully'));
            }
        } catch (error) {
            console.error('[WeakPasswordsPage] Error:', error);
            showError(error.message || 'Failed to load weak passwords');
        } finally {
            setLoading(false);
        }
    }, [effectiveDomainId, showError, showSuccess, t]);

    useEffect(() => {
        loadData(true);
    }, [effectiveDomainId]);

    const filteredData = useMemo(() => {
        const currentData = data || [];
        if (!search) return currentData;
        const lowerSearch = search.toLowerCase();
        return currentData.filter(item =>
            item.extension.toLowerCase().includes(lowerSearch) ||
            item.extensionName.toLowerCase().includes(lowerSearch) ||
            item.resource.toLowerCase().includes(lowerSearch)
        );
    }, [data, search]);

    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page, limit]);

    const columns = useMemo(() => [
        {
            key: 'extension',
            label: t('weakPasswords.extension', 'Extension'),
            visible: true,
            render: (_: unknown, item: WeakPasswordReport) => (
                <div>
                    <span className="font-medium text-foreground">{item.extension}</span>
                    {item.extensionName && (
                        <span className="ml-2 text-sm text-muted-foreground">({item.extensionName})</span>
                    )}
                </div>
            )
        },
        {
            key: 'type',
            label: t('weakPasswords.type', 'Type'),
            visible: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            render: (_: any, item: WeakPasswordReport) => {
                // Only device passwords are scanned (voicemail/features PINs are internal 4-digit codes)
                const icon = <Smartphone size={14} />;
                const color = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
                const label = 'SIP Device';

                return (
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${color}`}>
                        {icon}
                        {label}
                    </span>
                );
            }
        },
        {
            key: 'weakness',
            label: t('weakPasswords.weakness', 'Weakness'),
            visible: true,
            render: (_: unknown, item: WeakPasswordReport) => (
                <span className="text-red-600 dark:text-red-400 font-medium capitalize">
                    {item.weakness.replace('_', ' ')}
                </span>
            )
        },
        {
            key: 'resource',
            label: t('weakPasswords.resource', 'Resource ID'),
            visible: true,
            render: (_: unknown, item: WeakPasswordReport) => (
                <span className="font-mono text-xs">{item.resource}</span>
            )
        }
    ], [t]);

    return (
        <ModulePageContainer
            currentView="list"
            listView={
                <div className="pb-20">
                    <DataGridSimple
                        data={paginatedData}
                        columns={columns}
                        loading={loading}
                        pagination={{
                            page,
                            limit,
                            total: filteredData.length,
                            totalPages: Math.ceil(filteredData.length / limit)
                        }}
                        searchQuery={search}
                        onSearch={setSearch}
                        onPageChange={setPage}
                        onItemsPerPageChange={(newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        }}
                        onColumnToggle={() => { }}
                        onSort={() => { }}
                        sortBy="extension"
                        sortOrder="asc"
                        title={t('weakPasswords.title', 'Weak Passwords')}
                        titleIcon={<AlertTriangle className="h-5 w-5 text-orange-500" />}
                        renderActions={() => null}
                    />
                </div>
            }
            formView={null}
        >
            <FixedActionBar
                showSave={false}
                showCancel={false}
                additionalButtons={[
                    {
                        key: 'refresh',
                        label: t('common.checkNow', 'Check Now'),
                        icon: <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />,
                        onClick: loadData,
                        variant: 'default'
                    }
                ]}
            />
        </ModulePageContainer>
    );
}
